import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';
import type { LayoutChangeEvent, ScrollView } from 'react-native';

export type UseScrollSlotIntoViewResult = {
  scrollRef: RefObject<ScrollView | null>;
  onLayout: (e: LayoutChangeEvent) => void;
  onContentSizeChange: (contentWidth: number) => void;
  onChipLayout: (index: number, e: LayoutChangeEvent) => void;
};

/** Which edge the active chip is pulled toward, based on the direction of travel between tabs. */
export type ScrollDirection = 'forward' | 'backward';

/**
 * Scroll target that reveals the active chip in the direction the member is moving between tabs:
 * - `forward` (a later tab) pins the chip to the **left** edge, previewing the tabs after it;
 * - `backward` (an earlier tab) pins the chip to the **right** edge, previewing the tabs before it.
 * `padding` is the row's horizontal padding so the chip lands flush with the edge; the result is
 * clamped to `[0, maxOffset]` so an edge slot stays fully visible without forcing empty space.
 */
export function computeSlotScrollX(
  direction: ScrollDirection,
  chipX: number,
  chipWidth: number,
  padding: number,
  contentWidth: number,
  viewportWidth: number
): number {
  const maxOffset = Math.max(0, contentWidth - viewportWidth);
  const target =
    direction === 'backward' ? chipX + chipWidth + padding - viewportWidth : chipX - padding;
  return Math.min(Math.max(0, target), maxOffset);
}

/**
 * Scrolls the horizontal slot row so the active slot is revealed whenever `activeIndex` changes,
 * following the direction of travel between tabs (see computeSlotScrollX). Direction comes from the
 * index delta alone — no scroll-position or history tracking that could go stale — and a guard skips
 * a repeated run for the same index so it can never flip direction back. Because the effect keys
 * only on `activeIndex`, a manual scroll with no tab change is preserved (AC7) and content-only
 * re-renders (e.g. a spot label appearing) do not re-scroll. A mount that already starts on a
 * non-zero slot is handled separately by `alignActiveOnMount`.
 */
export function useScrollSlotIntoView(
  activeIndex: number,
  padding: number
): UseScrollSlotIntoViewResult {
  const scrollRef = useRef<ScrollView>(null);
  const chipMetricsRef = useRef<{ x: number; width: number }[]>([]);
  const viewportWidthRef = useRef(0);
  const contentWidthRef = useRef(0);
  const prevIndexRef = useRef(activeIndex);
  const activeIndexRef = useRef(activeIndex);
  const paddingRef = useRef(padding);
  // A mount that starts on a non-zero slot (the row remounting after a refetch, with the active
  // slot preserved) gets a fresh ScrollView sitting at offset 0, so the chip needs aligning once.
  const pendingMountAlignRef = useRef(activeIndex > 0);

  useEffect(() => {
    paddingRef.current = padding;
  }, [padding]);

  /**
   * Reveals the active chip on a mount that starts on a non-zero slot. The effect below only fires
   * on an index *change*, so it never covers this case. Runs from the measurement callbacks because
   * nothing is measured yet at mount time, and jumps without animation since it is restoring a
   * position rather than moving between tabs.
   */
  const alignActiveOnMount = useCallback(() => {
    if (!pendingMountAlignRef.current) return;
    const metrics = chipMetricsRef.current[activeIndexRef.current];
    if (metrics === undefined) return;
    // Both widths feed the clamp, so an unmeasured one would pin the row to offset 0.
    if (viewportWidthRef.current === 0 || contentWidthRef.current === 0) return;

    pendingMountAlignRef.current = false;
    const x = computeSlotScrollX(
      'forward',
      metrics.x,
      metrics.width,
      paddingRef.current,
      contentWidthRef.current,
      viewportWidthRef.current
    );
    scrollRef.current?.scrollTo({ x, animated: false });
  }, []);

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => {
      viewportWidthRef.current = e.nativeEvent.layout.width;
      alignActiveOnMount();
    },
    [alignActiveOnMount]
  );

  const onContentSizeChange = useCallback(
    (contentWidth: number) => {
      contentWidthRef.current = contentWidth;
      alignActiveOnMount();
    },
    [alignActiveOnMount]
  );

  const onChipLayout = useCallback(
    (index: number, e: LayoutChangeEvent) => {
      const { x, width } = e.nativeEvent.layout;
      chipMetricsRef.current[index] = { x, width };
      alignActiveOnMount();
    },
    [alignActiveOnMount]
  );

  useEffect(() => {
    activeIndexRef.current = activeIndex;
    const prevIndex = prevIndexRef.current;
    if (activeIndex === prevIndex) return;

    // Advanced before the bails so direction comes from the last active tab, not the last scroll.
    prevIndexRef.current = activeIndex;
    const metrics = chipMetricsRef.current[activeIndex];
    if (metrics === undefined) return;
    if (viewportWidthRef.current === 0) return;

    // The chip is about to be revealed, so a mount alignment still waiting on measurements is moot.
    pendingMountAlignRef.current = false;
    const direction: ScrollDirection = activeIndex < prevIndex ? 'backward' : 'forward';
    const x = computeSlotScrollX(
      direction,
      metrics.x,
      metrics.width,
      padding,
      contentWidthRef.current,
      viewportWidthRef.current
    );
    scrollRef.current?.scrollTo({ x, animated: true });
  }, [activeIndex, padding]);

  return useMemo(
    () => ({ scrollRef, onLayout, onContentSizeChange, onChipLayout }),
    [onLayout, onContentSizeChange, onChipLayout]
  );
}
